import { useState } from 'react';
import { Card, CardHeader } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { useVisibilityChats, useVisibilityChat } from '../../../../hooks/useVisibility';
import { useFilters } from '../filters';
import { PROVIDER_LABELS } from '../format';
import { EmptyState, LoadingState } from '../../../../components/ui/States';

export function ChatsPage() {
  const { startDate, endDate } = useFilters();
  const [provider, setProvider] = useState<string>('');
  const [citedOnly, setCitedOnly] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useVisibilityChats(startDate, endDate, {
    ...(provider ? { provider } : {}),
    ...(citedOnly ? { cited: true } : {}),
  });

  return (
    <Card padding="md">
      <CardHeader
        title="Chats"
        description="Browse every AI response the OS has collected"
        action={
          <div className="flex items-center gap-3 text-body-sm">
            <select value={provider} onChange={(e) => setProvider(e.target.value)} className="rounded-md border border-border-light bg-surface-primary px-2 py-1">
              <option value="">All engines</option>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Claude</option>
              <option value="perplexity">Perplexity</option>
              <option value="gemini">Gemini</option>
            </select>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={citedOnly} onChange={(e) => setCitedOnly(e.target.checked)} />
              Cited only
            </label>
          </div>
        }
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2 max-h-[640px] overflow-y-auto pr-2">
          {isLoading && <LoadingState />}
          {data && data.items.length === 0 && (
            <EmptyState compact title="No chats in this range with the chosen filters." />
          )}
          {data && data.items.slice(0, 80).map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c.id)}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${selected === c.id ? 'border-text-primary bg-surface-secondary' : 'border-border-light hover:bg-surface-secondary'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-body-xs font-medium">{PROVIDER_LABELS[c.provider] ?? c.provider} · {c.runDate}</span>
                {c.cited ? <Badge variant="success">Cited</Badge> : <Badge variant="default">Not cited</Badge>}
              </div>
              <p className="text-body-sm font-medium truncate">{c.probeQuery}</p>
              <p className="text-body-xs text-text-tertiary truncate">{c.preview}…</p>
            </button>
          ))}
        </div>
        <div>
          {selected ? <ChatDetail id={selected} /> : (
            <EmptyState compact title="Select a chat on the left to see the answer, mentions, and sources." />
          )}
        </div>
      </div>
    </Card>
  );
}

function ChatDetail({ id }: { id: string }) {
  const { data, isLoading } = useVisibilityChat(id);
  if (isLoading) return <LoadingState />;
  if (!data) return <p className="text-body-sm text-text-tertiary">Not found.</p>;

  const usageBits: string[] = [];
  if (data.result.tokensIn != null) usageBits.push(`${data.result.tokensIn.toLocaleString()} in`);
  if (data.result.tokensOut != null) usageBits.push(`${data.result.tokensOut.toLocaleString()} out`);
  if (data.result.latencyMs != null) usageBits.push(`${(data.result.latencyMs / 1000).toFixed(1)}s`);
  if (data.result.costUsd != null) usageBits.push(`$${data.result.costUsd.toFixed(4)}`);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-body-md font-medium">{data.result.probe.query}</p>
        <p className="text-body-xs text-text-tertiary mt-1">{PROVIDER_LABELS[data.result.llmProvider] ?? data.result.llmProvider} · {data.result.llmModel} · {data.result.runDate}</p>
        {usageBits.length > 0 && (
          <p className="mt-2 text-body-xs text-text-tertiary tabular-nums">Usage: {usageBits.join(' · ')}</p>
        )}
        <pre className="whitespace-pre-wrap text-body-sm text-text-secondary mt-3 max-h-72 overflow-y-auto border border-border-light rounded p-3 bg-surface-secondary/40">{data.result.responseText}</pre>
      </div>
      <div>
        <p className="text-body-xs font-medium text-text-secondary mb-1.5">Mentions ({data.mentions.length})</p>
        {data.mentions.length === 0 ? (
          <p className="text-body-sm text-text-tertiary">No brand mentions detected.</p>
        ) : (
          <ul className="space-y-2 text-body-sm">
            {data.mentions.map((m) => (
              <li key={m.id} className="flex items-start gap-2">
                <Badge>#{m.ordinal}</Badge>
                <div>
                  <p className="font-medium">{m.aliasMatched} {m.inList && m.listPosition ? `(list rank ${m.listPosition})` : ''}</p>
                  {m.sentiment != null && <p className="text-body-xs text-text-tertiary">Sentiment {m.sentiment}/100 — {m.sentimentNote}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="text-body-xs font-medium text-text-secondary mb-1.5">Sources ({data.sources.length})</p>
        {data.sources.length === 0 ? (
          <p className="text-body-sm text-text-tertiary">No sources cited.</p>
        ) : (
          <ul className="space-y-1 text-body-sm">
            {data.sources.map((s) => (
              <li key={s.id} className="flex items-center gap-2">
                <Badge>{s.domainClass ?? '—'}</Badge>
                <span className="text-body-xs text-text-tertiary">[{s.urlClassification ?? '—'}]</span>
                <a href={s.url} target="_blank" rel="noreferrer" className="truncate text-info-dark hover:text-info underline-offset-2 hover:underline">{s.url}</a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
